import React, { useEffect, useRef, useState } from 'react';
import { Language, Theme, TRANSLATIONS } from '../types';
import { supabase } from '../services/supabaseClient';

interface AccountSettingsProps {
  language: Language;
  theme: Theme;
  authUser: any | null;
  onBack: () => void;
  onUserUpdated: (user: any) => void;
}

export const AccountSettings: React.FC<AccountSettingsProps> = ({ language, theme, authUser, onBack, onUserUpdated }) => {
  const t = TRANSLATIONS[language];
  const isDark = theme === 'dark';
  const skeletonBlock = isDark ? 'bg-slate-800/70' : 'bg-slate-200';
  const nameInputId = 'account-name';
  const emailInputId = 'account-email';
  const phoneInputId = 'account-phone';
  const emailHintId = 'account-email-hint';
  const phoneHintId = 'account-phone-hint';
  const currentPasswordId = 'account-current-password';
  const newPasswordId = 'account-new-password';
  const confirmPasswordId = 'account-confirm-password';
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [phoneRefreshing, setPhoneRefreshing] = useState(false);
  const toastRef = useRef<HTMLDivElement | null>(null);
  const isGoogleUser = Boolean(
    authUser?.app_metadata?.provider === 'google' ||
      authUser?.app_metadata?.providers?.includes?.('google') ||
      authUser?.user_metadata?.iss?.includes?.('google')
  );

  if (!authUser) {
    return (
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className={`h-5 w-20 rounded-full ${skeletonBlock} animate-pulse`} />
            <div className={`mt-4 h-8 w-48 rounded-full ${skeletonBlock} animate-pulse`} />
          </div>
        </div>

        <div className="mt-10 space-y-10">
          <section className={`rounded-3xl border p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
            <div className={`h-5 w-40 rounded-full ${skeletonBlock} animate-pulse`} />
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className={`h-12 rounded-2xl ${skeletonBlock} animate-pulse`} />
              <div className={`h-12 rounded-2xl ${skeletonBlock} animate-pulse`} />
              <div className={`h-12 rounded-2xl ${skeletonBlock} animate-pulse md:col-span-2`} />
            </div>
          </section>

          <section className={`rounded-3xl border p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
            <div className={`h-5 w-32 rounded-full ${skeletonBlock} animate-pulse`} />
            <div className={`mt-4 h-10 w-44 rounded-full ${skeletonBlock} animate-pulse`} />
          </section>

          <div className="flex justify-end">
            <div className={`h-9 w-32 rounded-full ${skeletonBlock} animate-pulse`} />
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const name = authUser?.user_metadata?.full_name || authUser?.user_metadata?.name || '';
    const emailValue = authUser?.email || '';
    const phoneValue = authUser?.phone || authUser?.user_metadata?.phone || '';
    setFullName(name);
    setEmail(emailValue);
    setPhone(phoneValue);
  }, [authUser]);

  useEffect(() => {
    const refreshAuthPhone = async () => {
      if (!authUser) return;
      if (authUser.phone) return;
      if (phone.trim()) return;
      setPhoneRefreshing(true);
      const { data } = await supabase.auth.getUser();
      if (data?.user?.phone && data.user.phone !== phone) {
        setPhone(data.user.phone);
        onUserUpdated(data.user);
      }
      setPhoneRefreshing(false);
    };
    refreshAuthPhone();
  }, [authUser, onUserUpdated]);

  useEffect(() => {
    if (errorMessage) {
      toastRef.current?.focus();
    }
  }, [errorMessage]);

  const normalizePhone = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/[^\d]/g, '');
    return hasPlus ? `+${digits}` : digits;
  };

  const isValidPhone = (value: string) =>
    /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/.test(value);
  const isValidAuthPhone = (value: string) => /^\+[1-9]\d{6,14}$/.test(value);
  const phoneFormatErrorText = t.accountPhoneFormatError;
  const accountSaveErrorText = t.accountSaveError;

  const openPasswordModal = () => {
    setStatusMessage(null);
    setErrorMessage(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSave = async () => {
    setStatusMessage(null);
    setErrorMessage(null);
    setPhoneError(null);

    if (!authUser) {
      setErrorMessage(t.accountNotSignedIn);
      return;
    }

    const trimmedName = fullName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const trimmedCurrent = currentPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if ((trimmedCurrent || trimmedNew || trimmedConfirm) && trimmedCurrent.length < 8) {
      setErrorMessage(t.authPasswordMin);
      return;
    }
    if ((trimmedCurrent || trimmedNew || trimmedConfirm) && trimmedNew.length < 8) {
      setErrorMessage(t.authPasswordMin);
      return;
    }
    if ((trimmedCurrent || trimmedNew || trimmedConfirm) && trimmedConfirm.length < 8) {
      setErrorMessage(t.authPasswordMin);
      return;
    }
    if ((trimmedCurrent || trimmedNew || trimmedConfirm) && trimmedNew !== trimmedConfirm) {
      setErrorMessage(t.accountPasswordMismatch);
      return;
    }

    const updates: Record<string, any> = {};
    if (trimmedName && trimmedName !== (authUser?.user_metadata?.full_name || authUser?.user_metadata?.name || '')) {
      updates.data = { full_name: trimmedName };
    }

    const normalizedPhone = normalizePhone(trimmedPhone);
    const currentAuthPhone = normalizePhone(authUser?.phone || '');
    if (trimmedPhone) {
      if (!isValidPhone(trimmedPhone)) {
        setPhoneError(phoneFormatErrorText);
        setErrorMessage(phoneFormatErrorText);
        return;
      }
      if (!isValidAuthPhone(normalizedPhone)) {
        setPhoneError(phoneFormatErrorText);
        setErrorMessage(phoneFormatErrorText);
        return;
      }
      if (normalizedPhone !== currentAuthPhone) {
        updates.phone = normalizedPhone;
      }
    }
    const emailChanged = !isGoogleUser && trimmedEmail && trimmedEmail !== authUser?.email;
    if (emailChanged) {
      updates.email = trimmedEmail;
    }
    const needsReauth = Boolean(trimmedNew || emailChanged);
    if (needsReauth) {
      if (!trimmedCurrent) {
        setErrorMessage(t.accountCurrentPasswordRequired);
        return;
      }
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: authUser?.email,
        password: trimmedCurrent,
      });
      if (reauthError) {
        setErrorMessage(t.accountCurrentPasswordInvalid);
        return;
      }
    }
    if (trimmedNew) {
      updates.password = trimmedNew;
    }

    if (Object.keys(updates).length === 0) {
      setStatusMessage(t.accountNoChanges);
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      setErrorMessage(t.accountNotSignedIn);
      return;
    }

    const wantsPhoneUpdate = Boolean(trimmedPhone);
    const { data, error } = await supabase.functions.invoke('update-auth-user', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: {
        full_name: trimmedName || undefined,
        email: emailChanged ? trimmedEmail : undefined,
        password: trimmedNew || undefined,
        phone: updates.phone || undefined,
      },
    });
    if (error || !data?.ok) {
      if (wantsPhoneUpdate) {
        const rawDetails = data?.error || error?.message || '';
        const isDuplicate =
          typeof rawDetails === 'string' &&
          (rawDetails.includes('users_phone_key') || rawDetails.includes('duplicate key'));
        const phoneMessage = isDuplicate
          ? t.accountPhoneDuplicateError
          : t.accountPhoneFormatError;
        setPhoneError(phoneMessage);
        setErrorMessage(phoneMessage);
        return;
      }
      setErrorMessage(accountSaveErrorText);
      return;
    }

    const { data: refreshed } = await supabase.auth.getUser();
    if (refreshed?.user) {
      onUserUpdated(refreshed.user);
    }

    setStatusMessage(t.accountSaved);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordSave = async () => {
    setStatusMessage(null);
    setErrorMessage(null);

    if (!authUser) {
      setErrorMessage(t.accountNotSignedIn);
      return;
    }

    const trimmedCurrent = currentPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedCurrent) {
      setErrorMessage(t.accountCurrentPasswordRequired);
      return;
    }
    if (trimmedNew.length < 8 || trimmedConfirm.length < 8) {
      setErrorMessage(t.authPasswordMin);
      return;
    }
    if (trimmedNew !== trimmedConfirm) {
      setErrorMessage(t.accountPasswordMismatch);
      return;
    }

    const { error: reauthError } = await supabase.auth.signInWithPassword({
      email: authUser?.email,
      password: trimmedCurrent,
    });
    if (reauthError) {
      setErrorMessage(t.accountCurrentPasswordInvalid);
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      setErrorMessage(t.accountNotSignedIn);
      return;
    }

    const { data, error } = await supabase.functions.invoke('update-auth-user', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: {
        password: trimmedNew,
      },
    });
    if (error || !data?.ok) {
      setErrorMessage(accountSaveErrorText);
      return;
    }

    setStatusMessage(t.accountSaved);
    closePasswordModal();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 pb-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <span aria-hidden="true">←</span>
            {t.accountBack}
          </button>
          <h1 className={`mt-3 text-2xl font-semibold sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.accountTitle}</h1>
        </div>
        <div />
      </div>

      <div className="mt-10 space-y-10">
        <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.accountBasicInfo}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label htmlFor={nameInputId} className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountNameLabel}</label>
              <input
                id={nameInputId}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={`rounded-xl border px-4 py-3 text-sm outline-none ${isDark ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor={emailInputId} className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountEmailLabel}</label>
              <div className="relative">
                {isGoogleUser && (
                  <span className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 opacity-100">
                    <svg viewBox="-3 0 262 262" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" className="h-5 w-5 opacity-100">
                      <path d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" fill="#4285F4"></path>
                      <path d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" fill="#34A853"></path>
                      <path d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782" fill="#FBBC05"></path>
                      <path d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" fill="#EB4335"></path>
                    </svg>
                  </span>
                )}
                <input
                  id={emailInputId}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isGoogleUser}
                  aria-describedby={emailHintId}
                  className={`w-full rounded-xl border px-4 py-3 text-sm outline-none ${isDark ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'} ${isGoogleUser ? `cursor-not-allowed pl-10 ${isDark ? 'bg-slate-900/60 border-white/5 text-white/60' : 'bg-slate-100 border-slate-200 text-slate-900/60'}` : ''}`}
                />
              </div>
              <p id={emailHintId} className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                {isGoogleUser ? t.accountEmailGoogleHint : t.accountEmailHint}
              </p>
            </div>
            <div className="flex flex-col gap-2 md:col-span-1">
              <label htmlFor={phoneInputId} className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountPhoneLabel}</label>
              {phoneRefreshing ? (
                <div className={`h-11 rounded-xl ${isDark ? 'bg-slate-800/70' : 'bg-slate-200'} animate-pulse`} />
              ) : (
                <input
                  id={phoneInputId}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (phoneError) setPhoneError(null);
                  }}
                  aria-describedby={phoneHintId}
                  className={`rounded-xl border px-4 py-3 text-sm outline-none ${isDark ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                />
              )}
              {phoneError && (
                <p className="text-xs text-rose-300" role="alert">
                  {phoneError}
                </p>
              )}
              <p id={phoneHintId} className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                {t.accountPhoneHint}
              </p>
            </div>
          </div>
        </section>

        {!isGoogleUser && (
          <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.accountSecurityTitle}</h2>
            <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {t.accountSecurityDesc}
              </p>
              <button
                type="button"
                onClick={openPasswordModal}
                className={`rounded-full px-4 py-2 text-[11px] font-semibold transition sm:px-5 sm:text-xs ${isDark ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
              >
                {t.accountChangePassword}
              </button>
            </div>
          </section>
        )}

        {(statusMessage || errorMessage) && (
          <div
            role="alert"
            tabIndex={-1}
            ref={toastRef}
            className={`fixed right-6 top-[110px] z-[140] w-[min(360px,calc(100%-2rem))] rounded-2xl border px-4 py-3 text-sm shadow-2xl ${
              errorMessage
                ? 'border-rose-400/30 text-rose-100 bg-rose-500'
                : 'border-emerald-400/30 text-emerald-100 bg-emerald-600'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="pr-2">{errorMessage || statusMessage}</span>
              <button
                type="button"
                onClick={() => {
                  setErrorMessage(null);
                  setStatusMessage(null);
                }}
                className="text-white/80 hover:text-white"
                aria-label={t.authCloseLabel}
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            className="rounded-full bg-[#038759] px-4 py-2 text-xs font-semibold text-white sm:px-6 sm:text-sm"
          >
            {t.accountSave}
          </button>
        </div>
      </div>

      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closePasswordModal}
            aria-hidden="true"
          />
          <div className={`relative w-full max-w-lg rounded-3xl border p-5 shadow-2xl sm:p-6 ${isDark ? 'bg-black border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
            <h3 className="text-lg font-semibold sm:text-xl">{t.accountPasswordModalTitle}</h3>
            <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountPasswordHint}</p>
            <div className="mt-6 grid gap-4">
              <div className="flex flex-col gap-2">
                <label htmlFor={currentPasswordId} className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountCurrentPasswordLabel}</label>
                <input
                  id={currentPasswordId}
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t.accountCurrentPasswordPlaceholder}
                  className={`rounded-xl border px-4 py-3 text-sm outline-none ${isDark ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor={newPasswordId} className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountPasswordLabel}</label>
                <input
                  id={newPasswordId}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t.accountPasswordPlaceholder}
                  className={`rounded-xl border px-4 py-3 text-sm outline-none ${isDark ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor={confirmPasswordId} className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.accountPasswordConfirmLabel}</label>
                <input
                  id={confirmPasswordId}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t.accountPasswordConfirmPlaceholder}
                  className={`rounded-xl border px-4 py-3 text-sm outline-none ${isDark ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closePasswordModal}
                className={`rounded-full px-4 py-2 text-[11px] font-semibold transition sm:text-xs ${isDark ? 'border border-white/20 text-white hover:bg-white/10' : 'border border-slate-200 text-slate-700 hover:bg-slate-100'}`}
              >
                {t.contactCancel}
              </button>
              <button
                type="button"
                onClick={handlePasswordSave}
                className="rounded-full bg-[#038759] px-4 py-2 text-[11px] font-semibold text-white sm:text-xs"
              >
                {t.accountSavePassword}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
